import type { LinkedAccount, LinkedAccountPlatform } from "@store/linkedAccountSlice";
import { useLinkedAccounts } from "@store/linkedAccountSlice";
import { useMemo } from "react";
import { Trans } from "react-i18next";
import { LinkedAccountRow } from "./LinkedAccountRow";
import { PlatformLabel } from "./PlatformLabel";

interface PlatformGroup {
  platform: LinkedAccountPlatform;
  accounts: LinkedAccount[];
}

const groupAccountsByPlatform = (items: LinkedAccount[]): PlatformGroup[] => {
  const order: LinkedAccountPlatform[] = [];
  const map = new Map<LinkedAccountPlatform, LinkedAccount[]>();
  for (const account of items) {
    const platform = (account.platform ?? "TIKTOK") as LinkedAccountPlatform;
    if (!map.has(platform)) {
      order.push(platform);
      map.set(platform, []);
    }
    map.get(platform)!.push(account);
  }
  return order.map((platform) => ({ platform, accounts: map.get(platform)! }));
};

export const LinkedAccounts = () => {
  const { items } = useLinkedAccounts();
  const groups = useMemo(() => groupAccountsByPlatform(items), [items]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-[32px]">
      <div className="text-[16px] font-semibold leading-[22px] text-kds-fg">
        <Trans>Linked Accounts</Trans>
      </div>
      <div className="flex flex-col gap-[40px]">
        {groups.map(({ platform, accounts }) => (
          <div key={platform} className="flex items-start gap-[40px]">
            <PlatformLabel platform={platform} className="w-[120px]" />
            <div className="flex flex-col gap-[32px]">
              {accounts.map((account, index) => (
                <LinkedAccountRow key={account.id ?? `${platform}-${account.accountId}-${index}`} account={account} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
